import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Typography, Chip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';

const styles = theme => ({
  noteCard: {
    margin: '10px 0',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  noteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  noteDate: {
    color: theme.palette.text.secondary,
  },
  noteContent: {
    marginTop: 8,
    marginBottom: 8,
  },
  noteFooter: {
    marginTop: 8,
    color: theme.palette.text.secondary,
  },
  quickNoteChip: {
    marginLeft: 8,
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  taskLink: {
    marginTop: 8,
  },
  taskLinkText: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '0.875rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    marginBottom: 16,
    fontWeight: 500,
  },
});

const PipelineCardNotesTab = ({ notes, pipelineCardId, classes }) => {
  if (!notes || notes.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography variant="body1">
          No notes found for this case.
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <Typography variant="h6" className={classes.sectionTitle}>
        All Notes for this Case ({notes.length})
      </Typography>
      {notes.map(note => {
        const isQuickNote = note.time_spent && note.time_spent > 0;
        const quickNoteUrl = isQuickNote ? `/admin/quick_notes?quick_note_id=${note.id}` : null;
        
        return (
          <Card key={note.id} className={classes.noteCard}>
            <CardContent>
              <div className={classes.noteHeader}>
                <Typography variant="caption" className={classes.noteDate}>
                  {moment(note.created_at).format('MMM D, YYYY h:mm A')}
                </Typography>
                {isQuickNote && (
                  <a href={quickNoteUrl} style={{ textDecoration: 'none' }}>
                    <Chip 
                      label="Quick Note" 
                      size="small" 
                      color="primary" 
                      className={classes.quickNoteChip}
                    />
                  </a>
                )}
              </div>
              
              <Typography variant="body2" className={classes.noteContent}>
                {note.comment ? decodeURI(note.comment) : ''}
              </Typography>
              
              <div className={classes.noteFooter}>
                <Typography variant="caption">
                  By: {note.user?.name || 'Unknown'}
                  {note.activityType && ` | ${note.activityType.name}`}
                  {note.time_spent_formatted && ` | ${note.time_spent_formatted}`}
                  {note.planned !== null && note.planned !== undefined && (
                    ` | ${note.planned ? 'Planned' : 'Unplanned'}`
                  )}
                </Typography>
              </div>
              
              {note.task && (
                <div className={classes.taskLink}>
                  <Typography variant="caption">
                    Task: <a 
                      href={`/admin/tasks?task_id=${note.task.id}`}
                      className={classes.taskLinkText}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {note.task.name ? decodeURI(note.task.name) : 'Untitled Task'}
                    </a>
                  </Typography>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

PipelineCardNotesTab.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    comment: PropTypes.string,
    created_at: PropTypes.string,
    user: PropTypes.shape({
      name: PropTypes.string,
    }),
    activityType: PropTypes.shape({
      name: PropTypes.string,
    }),
    time_spent: PropTypes.number,
    time_spent_formatted: PropTypes.string,
    planned: PropTypes.bool,
    task: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  })),
  pipelineCardId: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

PipelineCardNotesTab.defaultProps = {
  notes: [],
  pipelineCardId: null,
};

export default withStyles(styles)(PipelineCardNotesTab);

